import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { PendingMoveEntry } from './types.ts'

export const useMapMoveStore = defineStore('map-move', () => {
  const entries = ref(new Map<number, PendingMoveEntry>())

  /**
   * Регистрирует объект в очереди ожидания подтверждения.
   * Если объект уже зарегистрирован — originalCoords не перезаписываются,
   * чтобы при повторных перемещениях откат происходил к самому первому положению.
   *
   * @param objectId - ID объекта
   * @param name - Отображаемое название объекта
   * @param originalCoords - Снимок координат до начала перемещения
   */
  const register = (objectId: number, name: string, originalCoords: any) => {
    if (entries.value.has(objectId)) return

    entries.value.set(objectId, {
      objectId,
      name,
      originalCoords,
      confirmFunc: () => {},
      cancelFunc: () => {},
    })
  }

  /**
   * Обновляет функции подтверждения и отмены для зарегистрированного объекта.
   * Вызывается после каждого dragEnd / clickAddPoint,
   * чтобы функции указывали на актуальные координаты.
   *
   * @param objectId - ID объекта
   * @param confirmFn - Функция сохранения новых координат в БД
   * @param cancelFn - Функция отката геометрии на карте
   */
  const updateActions = (objectId: number, confirmFn: () => void, cancelFn: () => void) => {
    const entry = entries.value.get(objectId)
    if (!entry) return

    entry.confirmFunc = confirmFn
    entry.cancelFunc = cancelFn
  }

  /**
   * Подтверждает перемещение одного объекта:
   * вызывает confirmFunc и удаляет запись из очереди.
   *
   * @param objectId - ID объекта
   */
  const confirmOne = (objectId: number) => {
    entries.value.get(objectId)?.confirmFunc()
    entries.value.delete(objectId)
  }

  /**
   * Отменяет перемещение одного объекта:
   * вызывает cancelFunc (откат геометрии) и удаляет запись из очереди.
   *
   * @param objectId - ID объекта
   */
  const cancelOne = (objectId: number) => {
    entries.value.get(objectId)?.cancelFunc()
    entries.value.delete(objectId)
  }

  /**
   * Подтверждает перемещение всех объектов в очереди
   * и полностью очищает очередь.
   */
  const confirmAll = () => {
    entries.value.forEach((entry) => entry.confirmFunc())
    entries.value.clear()
  }

  /**
   * Отменяет перемещение всех объектов в очереди,
   * откатывает геометрию каждого и очищает очередь.
   */
  const cancelAll = () => {
    entries.value.forEach((entry) => entry.cancelFunc())
    entries.value.clear()
  }

  /**
   * Проверяет, зарегистрирован ли объект в очереди ожидания.
   *
   * @param objectId - ID объекта
   */
  const has = (objectId: number): boolean => entries.value.has(objectId)

  return {
    entries,

    register,
    updateActions,
    confirmOne,
    cancelOne,
    confirmAll,
    cancelAll,
    has,
  }
})
